import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Eye, X } from 'lucide-react';
import type { Severity } from '@dxp/schema';
import { getInsights, qk, setInsightStatus, type Insight, type InsightStatus } from '../api.js';
import BilgiDugmesi from '../components/BilgiDugmesi.js';
import AtlasCard from '../components/AtlasCard.js';
import Badge from '../components/Badge.js';
import Card from '../components/Card.js';
import EmptyState, { ErrorState } from '../components/EmptyState.js';
import ImpactRange from '../components/ImpactRange.js';
import SeverityPill from '../components/SeverityPill.js';
import { LoadingBlock } from '../components/Spinner.js';
import { useActivePid } from '../components/Layout.js';
import DisaAktarDugmesi from '../components/DisaAktarDugmesi.js';
import { useRole } from '../components/AuthGate.js';
import { formatDate, formatImpactRange, formatNumber } from '../lib/format.js';

/**
 * §13.2 Bulgular — ürünün ANA sayfası.
 * priority sıralı insight kartları: başlık, teşhis, reçete, etki aralığı (TL/ay),
 * etkilenen oturum, kanıt metrikleri, örnek oturum linkleri, durum butonları.
 *
 * Görsel dil referans DXP admin paneliyle birebirdir (bkz. Funnel sayfası):
 * #fafafa zemin, beyaz başlık/filtre/stat şeritleri, rounded-2xl + #e5e7eb kartlar.
 */

const FILTERS: { key: InsightStatus | 'all'; label: string }[] = [
  { key: 'new', label: 'Yeni' },
  { key: 'seen', label: 'Gördüm' },
  { key: 'applied', label: 'Uygulandı' },
  { key: 'dismissed', label: 'Yoksayıldı' },
  { key: 'all', label: 'Tümü' },
];

const METRIC_LABELS: Record<string, string> = {
  errorRate: 'Hata oranı',
  errorSessions: 'Hatalı oturum',
  checkoutSessions: 'Checkout oturumu',
  status: 'HTTP kodu',
  dropPct: 'Kayıp %',
  medianDropPct: 'Medyan kayıp %',
  entered: 'Adıma giren',
  passed: 'Adımı geçen',
  ratio: 'Oran',
  totalSessions: 'Toplam oturum',
  rageSessions: 'Rage oturum',
  deadClickSessions: 'Ölü tık oturumu',
  lcpP75: 'LCP p75 (ms)',
  crSeen: 'Görenlerin CR’si',
  crNotSeen: 'Görmeyenlerin CR’si',
  mobileCr: 'Mobil CR',
  desktopCr: 'Masaüstü CR',
  zeroRate: 'Sıfır sonuç oranı',
  cartRate: 'Sepete ekleme oranı',
  affectedSessions: 'Etkilenen oturum',
};

/** Kartın sol kenar şeridi ciddiyeti taşır — listede tarama kolaylığı. */
const SEVERITY_EDGE: Record<Severity, string> = {
  critical: 'border-l-4 border-l-red-500',
  high: 'border-l-4 border-l-brand',
  medium: 'border-l-4 border-l-amber-400',
  low: 'border-l-4 border-l-[#d1d5db]',
};

function metricLabel(key: string): string {
  return METRIC_LABELS[key] ?? key;
}

function metricValue(key: string, value: number): string {
  const lower = key.toLowerCase();
  if (lower.includes('rate') || lower.endsWith('cr')) {
    return value <= 1 ? `%${(value * 100).toFixed(1)}` : formatNumber(value, 2);
  }
  if (lower.includes('pct')) return `%${value.toFixed(1)}`;
  return formatNumber(value, Number.isInteger(value) ? 0 : 2);
}

/** Filtre şeridi sekmesi — referans panelin aktif sekme dili (#fff4ec + brand). */
function FilterTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`h-8 whitespace-nowrap rounded-lg px-3 text-[13px] transition-colors ${
        active
          ? 'bg-[#fff4ec] font-medium text-brand'
          : 'text-[#6b7280] hover:bg-[#f3f4f6] hover:text-[#111827]'
      }`}
    >
      {children}
    </button>
  );
}

export default function Insights() {
  const pid = useActivePid();
  const role = useRole(pid);
  const [filter, setFilter] = useState<InsightStatus | 'all'>('all');
  const queryClient = useQueryClient();

  const status = filter === 'all' ? undefined : filter;
  const query = useQuery({
    queryKey: qk.insights(pid, status),
    queryFn: ({ signal }) => getInsights(pid, status, signal),
  });

  const mutation = useMutation({
    mutationFn: ({ id, next }: { id: number; next: InsightStatus }) => setInsightStatus(id, next),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['insights'] });
      void queryClient.invalidateQueries({ queryKey: ['overview'] });
    },
  });

  const insights = query.data?.items ?? [];
  // §33.5 — bulgu listesi tavana değdiyse toplam etki/sayılar EKSİK hesaplanır.
  const kirpildi = query.data?.truncated === true;

  const criticalCount = insights.filter((i) => i.severity === 'critical').length;
  const affectedTotal = insights.reduce((sum, i) => sum + i.affected_sessions, 0);

  // §12.2 — toplam etki de tek sayı değil, aralık olarak yazılır.
  const known = insights.filter((i) => i.impact_low !== null && i.impact_high !== null);
  const totalImpact =
    known.length > 0
      ? formatImpactRange(
          known.reduce((sum, i) => sum + (i.impact_low ?? 0), 0),
          known.reduce((sum, i) => sum + (i.impact_high ?? 0), 0),
        )
      : 'etki hesaplanmaz';

  const isEmpty = !query.isLoading && !query.isError && insights.length === 0;

  return (
    <div className="min-h-full bg-[#fafafa]" data-testid="bulgular-sayfasi">
      {/* başlık şeridi */}
      <div className="flex items-center justify-between border-b border-[#e5e7eb] bg-white px-6 py-4">
        <div className="flex items-center gap-2">
          <h1 className="font-grotesk text-[18px] font-bold text-[#111827]">Bulgular</h1>
          <BilgiDugmesi testId="bulgular-bilgi">Önceliğe göre sıralıdır (§12.3): en üstteki, TL etkisi en yüksek olan iştir. Etki her zaman aralık olarak gösterilir.</BilgiDugmesi>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden text-[12px] text-[#9ca3af] md:inline">
            Önceliğe göre sıralı — en üstteki, TL etkisi en yüksek olan iştir
          </span>
          {/* §95.1 — "gördüğünü indir": aktif durum filtresi export'a aynen gider. */}
          <DisaAktarDugmesi
            pid={pid}
            role={role}
            dataset="insights"
            params={{ status: filter === 'all' ? undefined : filter }}
          />
        </div>
      </div>

      {/* filtre şeridi */}
      <nav
        aria-label="Durum filtresi"
        className="flex flex-wrap items-center gap-1 border-b border-[#e5e7eb] bg-white px-6 py-3"
      >
        {FILTERS.map((f) => (
          <FilterTab key={f.key} active={filter === f.key} onClick={() => setFilter(f.key)}>
            {f.label}
          </FilterTab>
        ))}
      </nav>

      {/* istatistik şeridi */}
      <div className="flex flex-wrap items-center gap-8 border-b border-[#e5e7eb] bg-white px-6 py-2.5">
        {query.isLoading ? (
          <span className="animate-pulse text-[12px] text-[#9ca3af]">Veri yükleniyor…</span>
        ) : (
          [
            { label: 'Bulgu', value: formatNumber(insights.length) },
            { label: 'Kritik', value: formatNumber(criticalCount), accent: criticalCount > 0 },
            { label: 'Etkilenen oturum', value: formatNumber(affectedTotal) },
            { label: 'Toplam etki', value: totalImpact },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-2">
              <span className="text-[12px] text-[#9ca3af]">{s.label}</span>
              <span
                className={`num text-[13px] font-semibold ${
                  s.accent === true ? 'text-brand' : 'text-[#111827]'
                }`}
              >
                {s.value}
              </span>
            </div>
          ))
        )}

        {/*
          §33.5 — kırpma AÇIKÇA söylenir. "Toplam etki" ve "Etkilenen oturum" yalnız
          GÖSTERİLEN bulgulardan hesaplanır; liste kırpıldıysa bu sayılar EKSİKTİR ve
          uyarısız hâlde tam sanılır.
        */}
        {kirpildi ? (
          <span
            data-testid="bulgu-listesi-kirpildi"
            title={`Sunucu bu uçta en çok ${String(query.data?.limit ?? 0)} bulgu döndürür; aşağıdaki toplamlar yalnız gösterilenleri kapsar.`}
            className="rounded-md border border-[#fde68a] bg-[#fffbeb] px-2 py-0.5 text-[11px] font-medium text-[#92400e]"
          >
            İlk {formatNumber(query.data?.limit ?? 0)} bulgu gösteriliyor
          </span>
        ) : null}
      </div>

      {/* içerik */}
      <div className="px-6 py-8">
        {/* §25.7 — Atlas özeti Bulgular listesinin ÜSTÜNDE durur. */}
        <AtlasCard pid={pid} />
        {query.isLoading ? <LoadingBlock label="Bulgular yükleniyor…" /> : null}

        {query.isError ? (
          <ErrorState
            title="Bulgular alınamadı"
            description={(query.error as Error).message}
            onRetry={() => void query.refetch()}
          />
        ) : null}

        {isEmpty ? (
          <EmptyState
            title="Henüz bulgu yok"
            description="Analiz 5 dakikada bir çalışır. Trafik biriktikçe teşhisler burada TL etkisiyle listelenir."
          />
        ) : null}

        {insights.length > 0 ? (
          <div className="space-y-4">
            {insights.map((insight) => (
              <InsightCard
                key={insight.id}
                insight={insight}
                pending={mutation.isPending && mutation.variables?.id === insight.id}
                onStatus={(next) => mutation.mutate({ id: insight.id, next })}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * §12.4 etki doğrulama rozeti — ÜÇ HÂL, üçü de gerçek veriden.
 *
 * Eskiden burası koşulsuz "etki doğrulama: 14 gün sonra" yazıyordu; ölçüm hiç
 * yazılmadığı için bu, tutulmayan bir sözdü. Artık:
 *  - ölçüm YAPILMIŞ + kural susmuş  → "doğrulandı: sorun kayboldu" (yeşil)
 *  - ölçüm YAPILMIŞ + kural sürüyor → "doğrulama: %X" (azalma yeşil, artış sarı)
 *  - ölçüm BEKLİYOR                 → kalan gün sayısıyla birlikte beklemede
 */
export function VerificationBadge({ insight }: { insight: Insight }) {
  const after = insight.evidence?.after;

  if (after === undefined) {
    // Beklemede: kalan günü GERÇEKTEN hesapla — sabit "14 gün sonra" metni,
    // 13. günde de 1. günde de aynı şeyi söyleyerek bilgi taşımıyordu.
    const appliedMs = insight.applied_at ? Date.parse(insight.applied_at) : Number.NaN;
    if (!Number.isFinite(appliedMs)) {
      return (
        <Badge tone="petrol" size="sm">
          etki doğrulama: bekliyor
        </Badge>
      );
    }
    const elapsedDays = Math.floor((Date.now() - appliedMs) / 86_400_000);
    const remaining = Math.max(0, 14 - elapsedDays);
    return (
      <Badge tone="petrol" size="sm" title={`Uygulandı: ${formatDate(insight.applied_at)}`}>
        {remaining === 0 ? 'etki doğrulama: ölçülüyor' : `etki doğrulama: ${remaining} gün sonra`}
      </Badge>
    );
  }

  const title = `Ölçüm: ${formatDate(after.at)} · ${after.windowDays} günlük pencere`;

  if (!after.stillDetected) {
    return (
      <Badge tone="success" size="sm" title={title}>
        doğrulandı: sorun kayboldu
      </Badge>
    );
  }

  if (after.changePct === null) {
    return (
      <Badge tone="warning" size="sm" title={title}>
        doğrulandı: sorun sürüyor
      </Badge>
    );
  }

  // NEGATİF değişim İYİDİR (etkilenen oturum azaldı).
  const improved = after.changePct < 0;
  return (
    <Badge tone={improved ? 'success' : 'warning'} size="sm" title={title}>
      {`doğrulandı: etkilenen oturum ${improved ? '' : '+'}${formatNumber(after.changePct, 1)}%`}
    </Badge>
  );
}

interface InsightCardProps {
  insight: Insight;
  pending: boolean;
  onStatus: (next: InsightStatus) => void;
}

export function InsightCard({ insight, pending, onStatus }: InsightCardProps) {
  const metrics = insight.evidence?.metrics ?? {};
  const samples = insight.evidence?.sampleSessionIds ?? [];
  const isInfo = insight.impact_tl_month === null || insight.impact_tl_month === undefined;

  return (
    <Card className={SEVERITY_EDGE[insight.severity]}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <SeverityPill severity={insight.severity} />
            <Badge tone="neutral" size="sm" title="Kural kimliği">
              {insight.rule_id}
            </Badge>
            {insight.status === 'applied' ? <VerificationBadge insight={insight} /> : null}
            {isInfo ? (
              <Badge tone="neutral" size="sm" title="§12.3 — bu kural etki hesaplamaz">
                bilgi
              </Badge>
            ) : null}
          </div>

          <h2 className="mt-2 text-[15px] font-semibold text-[#111827]">{insight.title}</h2>

          {/* DİKKAT: e2e `innerText` içinde "Teşhis"/"Reçete" harfi harfine arar.
              Chrome innerText'e `text-transform` uygular — bu iki etikete ASLA
              `uppercase` verme, aksi hâlde metin "TEŞHİS" olur ve test kırılır. */}
          <dl className="mt-3 space-y-2.5">
            <div>
              <dt className="text-[12px] font-semibold text-[#9ca3af]">Teşhis</dt>
              <dd className="mt-0.5 text-[13px] text-[#374151]">{insight.diagnosis}</dd>
            </div>
            <div>
              <dt className="text-[12px] font-semibold text-[#9ca3af]">Reçete</dt>
              <dd className="mt-0.5 text-[13px] text-[#374151]">{insight.prescription}</dd>
            </div>
          </dl>
        </div>

        <div className="shrink-0 text-right">
          {isInfo ? (
            <p className="text-[13px] text-[#9ca3af]">etki hesaplanmaz</p>
          ) : (
            /* §12.2 — etki HER ZAMAN aralık olarak gösterilir, asla tek sayı. */
            <ImpactRange
              low={insight.impact_low}
              high={insight.impact_high}
              mid={insight.impact_tl_month}
              size="lg"
            />
          )}
          <p className="num mt-1 text-[12px] text-[#6b7280]">
            {formatNumber(insight.affected_sessions)} oturum etkileniyor
          </p>
          <p className="num text-[11px] text-[#9ca3af]">efor {insight.effort}/5</p>
        </div>
      </div>

      {Object.keys(metrics).length > 0 ? (
        <div className="mt-4 rounded-xl border border-[#f3f4f6] bg-[#fafafa] p-3">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#9ca3af]">
            Kanıt metrikleri
          </p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
            {Object.entries(metrics).map(([key, value]) => (
              <div key={key} className="flex justify-between gap-2">
                <span className="truncate text-[12px] text-[#6b7280]">{metricLabel(key)}</span>
                <span className="num text-[13px] font-medium text-[#111827]">
                  {metricValue(key, Number(value))}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        {samples.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[12px] text-[#9ca3af]">Örnek oturumlar:</span>
            {samples.slice(0, 5).map((sid) => (
              <Link
                key={sid}
                to={`/oturumlar?sid=${encodeURIComponent(sid)}`}
                className="rounded-md bg-[#f3f4f6] px-2 py-0.5 font-mono text-[11px] text-[#374151] transition-colors hover:bg-[#fff4ec] hover:text-brand"
              >
                {sid.slice(0, 8)}
              </Link>
            ))}
          </div>
        ) : (
          <span />
        )}

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-ghost"
            onClick={() => onStatus('seen')}
            disabled={pending}
          >
            <Eye aria-hidden="true" className="h-3.5 w-3.5 text-[#9ca3af]" />
            Gördüm
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => onStatus('applied')}
            disabled={pending}
          >
            <Check aria-hidden="true" className="h-3.5 w-3.5" />
            Uyguladım
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => onStatus('dismissed')}
            disabled={pending}
          >
            <X aria-hidden="true" className="h-3.5 w-3.5 text-[#9ca3af]" />
            Yoksay
          </button>
        </div>
      </div>
    </Card>
  );
}
